'use client';

import { useCallback } from 'react';
import { decodeName, User, UserAccount } from '@drift-labs/sdk';
import { COMMON_UI_UTILS } from '@drift/common';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftAccountsStore, {
	ACCOUNT_INITIAL_STATE,
	AccountData,
} from 'src/stores/useDriftAccountsStore';
import { DEFAULT_PUBLIC_KEY } from 'src/@types/types';

export const useGenerateInitialStateForUserAccount = () => {
	const getAccountsState = useDriftAccountsStore((s) => s.get);
	const getDriftStore = useDriftStore((s) => s.get);
	const generateInitialStateForUserAccount = useCallback(
		(userClient: User, userAccount: UserAccount): AccountData => {
			const walletAdapter = getDriftStore().wallet.current.adapter;
			if (!walletAdapter?.publicKey) return ACCOUNT_INITIAL_STATE();
			const accountsState = getAccountsState();
			const userAccountToSubscribe =
				userClient?.getUserAccount() ?? userAccount;
			const userId = userAccountToSubscribe.subAccountId;
			const userKey = COMMON_UI_UTILS.getUserKey(
				userId,
				userAccountToSubscribe.authority
			);

			const accountData: AccountData = accountsState.accounts[
				userKey
			] as AccountData;
			let accountState;

			// make sure we don't overwrite account data if it's already loaded
			if (accountData && accountData?.isSubscribed) {
				accountState = accountData;
			} else {
				// initialize account state in store
				accountState = ACCOUNT_INITIAL_STATE(userKey);

				accountState = {
					...accountState,
					userKey: userKey,
					userId: userId,
					name: decodeName(userAccountToSubscribe.name),
					pubKey: userClient.getUserAccountPublicKey(),
					authority: userAccountToSubscribe.authority,
					isSubscribed: true,
					client: userClient,
					marginEnabled: userClient.getUserAccount().isMarginTradingEnabled,
					delegate: userAccount.delegate,
					isDelegatedTo: userAccount.delegate.equals(walletAdapter.publicKey),
					isDelegatedAway:
						!userAccount.delegate.equals(walletAdapter.publicKey) &&
						!userAccount.delegate.equals(DEFAULT_PUBLIC_KEY),
					poolId: userAccount.poolId,
				};
			}

			return accountState;
		},
		[getAccountsState]
	);

	return generateInitialStateForUserAccount;
};
